/**
 * „Przypisane ankiety" — the check-ins this client is enrolled in, read off the templates list
 * (`assignedCustomerIds`), and which empty sentence the Ankiety pane says when the history is empty.
 *
 * An assignment opens no row in the client's HISTORY until the backend's first cycle is sent, so
 * without this the tab read „nie ma jeszcze ankiet" straight after a successful assignment. The
 * mobile app solves it the same way: a list of what is assigned, and a pane that says it is waiting.
 *
 * No React, no DOM, no query hook, no i18next: `t` arrives as a parameter.
 */

import { cadenceLabel, type SurveyGroups, type TranslateKey } from '~/entities/survey-template';

export interface EnrolledSurvey {
  readonly id: number;
  readonly title: string;
  /** „co tydz · pon", or `null` when the wire carried no rhythm. */
  readonly cadence: string | null;
  readonly paused: boolean;
}

/** Only check-ins: the intake is every client's and is assigned at onboarding, not by the trainer. */
export function enrolledSurveys(
  groups: SurveyGroups,
  clientId: number,
  t: TranslateKey,
): readonly EnrolledSurvey[] {
  return groups.checkIns
    .filter((row) => row.assignedCustomerIds.includes(clientId))
    .map((row) => ({
      id: row.id,
      title: row.title,
      cadence: cadenceLabel(row, t),
      paused: !row.active,
    }));
}

/**
 * The pane's sentence for a client with no history: `awaiting` once something is assigned,
 * `none` otherwise — and `none` too when the list could not be read (`null`), because „czeka na
 * pierwsze odpowiedzi" is a claim about an assignment nobody has confirmed.
 */
export type SurveysEmptyKind = 'none' | 'awaiting';

export function surveysEmptyKind(enrolled: readonly EnrolledSurvey[] | null): SurveysEmptyKind {
  return enrolled !== null && enrolled.length > 0 ? 'awaiting' : 'none';
}
